import { Alert, Button, Modal, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from "react";
import { useFetcher, useLoaderData } from 'react-router-dom';
import { uploadFileFromObject } from "../../../api/common";
import { Box } from "../../../components/design";
import { InputWrapper } from "../../../components/inputs/input-wrappers.jsx";
import { useUpdateQueryStringValueWithoutNavigation } from "../../../hooks/use-update-query-string-without-navigation.js";
import { formatDate } from '../../../library/utils/intl.js';

import MultifileUpload from "../../../ui/multifile-upload.jsx";
import styles from './intransit.module.scss';
import { getAdjustedDeliveryDays } from '../../../library/utils/deliverydate.js';

const DeliveryConfirm = ({ item, onConfirm }) => {
    const address =
        <>{item?.street_number},{item?.street},{item?.city}</>

    return (
        <div className={styles.deliveryRow}>
            <div className="quantity">{item.quantity}</div>
            <div className="address">{address}</div>
            <div className="field-quantity">{item.ship_quantity}</div>
            <div className="field-expected_date">
                {formatDate(item.dispatch_date)}
            </div>
            <div className="field-trackingurl"><div><a href={item.tracking_url} target="blank" rel="noreferrer noopener">View</a></div></div>
            <div className="field-expected_date">{formatDate(item.expected_date)}</div>
            {item.status === 4 ? (
                <Button
                    type="primary"
                    name="action"
                    disabled
                >Proof Sent</Button>
            ) : (
                <Button
                    type="primary"
                    name="action"
                    value="confirmDeliveryByOnlyVendor"
                    onClick={onConfirm}
                >Confirm</Button>
            )}

        </div>
    )
}
DeliveryConfirm.propTypes = {
    item: PropTypes.object.isRequired,
    onConfirm: PropTypes.func.isRequired,
}

const InTransit = () => {
    const { productionDetails } = useLoaderData()
    const fetcher = useFetcher();
    const { errors = {} } = fetcher.data || {}
    const fileUploaderRef = useRef([])
    const inputComment = useRef('')
    const [isUploading, setIsUploading] = useState(false)
    const expectedDeliveryDays = getAdjustedDeliveryDays(productionDetails.item_delivery_address.find((delivery) => delivery)?.expected_date)

    const [confirmDeliveryId, setConfirmDeliveryId] = useUpdateQueryStringValueWithoutNavigation('confirmdelivery')
    useEffect(() => {
        setIsUploading(fetcher.json?.action === 'confirmDelivery')
    }, [fetcher.state, fetcher.data])

    const handleFilesUpload = async () => {
        if (fileUploaderRef.current.length === 0) {
            alert('Please select atleast one file')
            return
        }
        setIsUploading(true)

        const promises = fileUploaderRef.current.map(file => uploadFileFromObject(file))
        try {
            const fileIds = await Promise.all(promises)
            fetcher.submit({
                image_ids: fileIds,
                comment: inputComment.current,
                action: 'confirmDeliveryByOnlyVendor'
            }, {
                method: 'post',
                encType: 'application/json'
            })
            closeConfirmDeliveryPopup()
        }
        catch (error) {
            message.error(error.message)
            return
        }
        finally {
            setIsUploading(true)
        }
    }

    const closeConfirmDeliveryPopup = () => {
        if (isUploading && confirmDeliveryId) return //prevent closing popup while file is being uploaded
        setConfirmDeliveryId(null)
        inputComment.current = ''
        fileUploaderRef.current = []
    };

    return (
        <div className={styles.container}>
            <h3>Task For You</h3>
            <Alert
                description={
                    expectedDeliveryDays === 0
                        ? `You have shipped the order, the delivery is expected today.`
                        : `You have shipped the order, the delivery will take ${expectedDeliveryDays} to ${expectedDeliveryDays + 1} days.`
                }
                type="info"
                showIcon
            />
            <Box header={
                <div className={styles.deliveryRowHead}>
                    <div>Quantity</div>
                    <div>Delivery Address</div>
                    <div>Ship Quantity</div>
                    <div>Expected Dispatch Date</div>
                    <div>Tracking URL</div>
                    <div>Expected Delivery Date</div>
                    <div></div>
                </div>
            }>
                <DeliveryConfirm
                    item={productionDetails?.item_delivery_address?.[0]}
                    onConfirm={() => {
                        setConfirmDeliveryId(productionDetails?.item_delivery_address?.[0]?.id)
                    }}
                />
            </Box>
            <Modal
                title="Upload Proof of Delivery"
                open={confirmDeliveryId}
                onOk={() => handleFilesUpload()}
                confirmLoading={isUploading}
                okText="Continue"
                onCancel={closeConfirmDeliveryPopup}
            >
                <div className={styles.uploadBox}>
                    <p>The uploaded printscreen should not contain track and trace code, country of origin and sender information</p>
                    <div className="main">
                        <div className="box--white">
                            <MultifileUpload ref={fileUploaderRef} />
                        </div>
                    </div>
                    <br />
                    <InputWrapper name="comment" label="Comment" defaultValue="" textarea allowClear errors={errors} onChange={e => inputComment.current = e.target.value} />
                </div>
            </Modal>
        </div>
    )
}

export default InTransit;